// <s-product-gallery> switches the single visible product media slide from a thumbnail tablist.
// Markup contract: [data-gallery-slide] and [data-gallery-thumb] pairs share data-media-id.
// Optional [data-gallery-prev] / [data-gallery-next] buttons and horizontal swipes on
// [data-gallery-stage] step through the media in thumbnail order.
class SProductGallery extends HTMLElement {
  #ac;
  #startX = null;
  #startY = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.addEventListener('click', this.#onClick, opts);
    this.addEventListener('keydown', this.#onKeydown, opts);
    const stage = this.querySelector('[data-gallery-stage]');
    if (stage) {
      stage.addEventListener('pointerdown', this.#onPointerDown, opts);
      stage.addEventListener('pointerup', this.#onPointerUp, opts);
      stage.addEventListener('pointercancel', () => { this.#startX = null; }, opts);
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  get #thumbs() {
    return [...this.querySelectorAll('[data-gallery-thumb]')];
  }

  get #slides() {
    return [...this.querySelectorAll('[data-gallery-slide]')];
  }

  #currentIndex() {
    return this.#slides.findIndex((slide) => !slide.hidden);
  }

  step(delta) {
    const slides = this.#slides;
    if (slides.length < 2) return;
    const index = (this.#currentIndex() + delta + slides.length) % slides.length;
    this.select(slides[index].dataset.mediaId);
  }

  #onPointerDown = (event) => {
    if (event.pointerType === 'mouse') return;
    this.#startX = event.clientX;
    this.#startY = event.clientY;
  };

  #onPointerUp = (event) => {
    if (this.#startX === null) return;
    const dx = event.clientX - this.#startX;
    const dy = event.clientY - this.#startY;
    this.#startX = null;
    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
    const rtl = document.documentElement.dir === 'rtl';
    this.#suppressClick = true;
    this.step((dx < 0) !== rtl ? 1 : -1);
  };

  #suppressClick = false;

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (this.#suppressClick) {
      this.#suppressClick = false;
      if (event.target.closest('[data-gallery-stage]')) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
    }
    if (event.target.closest('[data-gallery-prev]')) return this.step(-1);
    if (event.target.closest('[data-gallery-next]')) return this.step(1);
    const thumb = event.target.closest('[data-gallery-thumb]');
    if (!thumb || !this.contains(thumb)) return;
    this.select(thumb.dataset.mediaId);
  };

  #onKeydown = (event) => {
    if (!(event.target instanceof Element)) return;
    const current = event.target.closest('[data-gallery-thumb]');
    if (!current) return;
    const thumbs = this.#thumbs;
    const index = thumbs.indexOf(current);
    let next = null;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = thumbs[(index + 1) % thumbs.length];
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = thumbs[(index - 1 + thumbs.length) % thumbs.length];
    else if (event.key === 'Home') next = thumbs[0];
    else if (event.key === 'End') next = thumbs[thumbs.length - 1];
    if (!next) return;
    event.preventDefault();
    if (document.documentElement.dir === 'rtl' && (event.key === 'ArrowRight' || event.key === 'ArrowLeft')) {
      next = event.key === 'ArrowRight' ? thumbs[(index - 1 + thumbs.length) % thumbs.length] : thumbs[(index + 1) % thumbs.length];
    }
    next.focus();
    this.select(next.dataset.mediaId);
  };

  async select(mediaId) {
    const slides = this.#slides;
    const incoming = slides.find((slide) => slide.dataset.mediaId === mediaId);
    const outgoing = slides.find((slide) => !slide.hidden);
    if (!incoming || incoming === outgoing) return;

    for (const thumb of this.#thumbs) {
      const selected = thumb.dataset.mediaId === mediaId;
      thumb.setAttribute('aria-selected', String(selected));
      thumb.tabIndex = selected ? 0 : -1;
      if (selected) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }

    const status = this.querySelector('[data-gallery-status]');
    if (status) status.textContent = `${slides.indexOf(incoming) + 1} / ${slides.length}`;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (outgoing && !reduceMotion) {
      await outgoing.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 150, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }).finished;
    }
    if (outgoing) outgoing.hidden = true;
    incoming.hidden = false;
    if (!reduceMotion) {
      incoming.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 220, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' });
    }
  }
}

if (!customElements.get('s-product-gallery')) customElements.define('s-product-gallery', SProductGallery);
